import { girarEmTorno, vetor } from '../geometria/vetor3'
import {
  ESPESSURA_PAINEL_TETO,
  alturaTampaTraseira,
  yFaceInternaDoTeto,
  type AncoraOcupante,
  type PerfilVeiculo,
} from './perfilVeiculo'
import type { ProporcoesVitima } from './perfisVitima'
import { colocacaoNaAncora, type ColocacaoVitima } from './rigVitima'

/**
 * Em que posição o veículo parou.
 *
 * O capotamento não muda a matemática da retirada: muda a posição da vítima e a
 * orientação do veículo. Por isso tudo continua calculado no referencial do
 * veículo, e a rolagem é transformação de desenho — o ângulo, os obstáculos e a
 * rota saem das mesmas funções nas duas configurações.
 */
export type ConfiguracaoVeiculo = 'sobreRodas' | 'lateral' | 'invertido'

export const CONFIGURACOES_VEICULO: readonly ConfiguracaoVeiculo[] = [
  'sobreRodas',
  'lateral',
  'invertido',
]

/**
 * Configurações com retirada modelada.
 *
 * As três posições usam o mesmo referencial interno. No tombado, o lado esquerdo
 * fica contra o solo e a retirada deve usar o lado alto ou a traseira.
 */
export const CONFIGURACOES_COM_EXTRACAO: readonly ConfiguracaoVeiculo[] = [
  'sobreRodas',
  'lateral',
  'invertido',
]

export const NOMES_CONFIGURACAO: Record<ConfiguracaoVeiculo, string> = {
  sobreRodas: 'Sobre as rodas',
  lateral: 'Tombado (lado esquerdo)',
  invertido: 'Capotado (invertido)',
}

/** Decúbito da vítima deitada no teto do veículo capotado. */
export type Decubito = 'ventral' | 'dorsal'

export const DECUBITOS: readonly Decubito[] = ['dorsal', 'ventral']

export const NOMES_DECUBITO: Record<Decubito, string> = {
  dorsal: 'Decúbito dorsal (de costas para o teto)',
  ventral: 'Decúbito ventral (de frente para o teto)',
}

/** Para onde aponta a cabeça da vítima deitada, no eixo do veículo. */
export type SentidoCabeca = 'frente' | 'traseira'

export const NOMES_SENTIDO_CABECA: Record<SentidoCabeca, string> = {
  frente: 'Cabeça voltada para a frente do veículo',
  traseira: 'Cabeça voltada para a traseira do veículo',
}

/**
 * Depois da rolagem de 180° (Euler XYZ), inclinação positiva em X deixa +Z
 * — a frente/cofre — mais baixa. O sinal oposto apoiava indevidamente a traseira.
 *
 * 20°, não os 8° de antes: a 8° a diferença de altura entre a frente encostada
 * e a traseira elevada é pequena demais para o aluno perceber de relance — a
 * cena parecia o teto inteiro apoiado no chão, quando o que apoia de fato é só
 * o cofre do motor. A inclinação precisa ser inconfundível.
 */
export const INCLINACAO_CAPOTADO_RAD = (20 * Math.PI) / 180

/**
 * Silhueta superior do veículo, de trás para frente: baixa na tampa traseira,
 * sobe para a altura do teto (só no vão em que ele existe de fato) e desce
 * outra vez para a altura do capô na frente.
 *
 * Não é uma caixa lisa na altura do teto do para-choque ao para-choque: essa
 * caixa tem um canto — teto × extremo dianteiro — que não existe no carro
 * real (o capô é bem mais baixo que o teto). Invertido e inclinado, era esse
 * canto fantasma que encostava no chão primeiro, deixando o capô — que é o
 * que de fato apoia no capotado — flutuando bem acima do solo.
 */
function silhuetaSuperior(perfil: PerfilVeiculo): ReadonlyArray<readonly [z: number, y: number]> {
  const tetoExterno = perfil.tetoY + ESPESSURA_PAINEL_TETO / 2
  return [
    [perfil.traseiraZ, alturaTampaTraseira(perfil)],
    [perfil.teto.zTraseiro, tetoExterno],
    [perfil.teto.zDianteiro, tetoExterno],
    [perfil.frenteZ, perfil.capoY],
  ]
}

/**
 * Altura de apoio exata para qualquer combinação de rolagem e inclinação.
 *
 * Gira os vértices da silhueta do veículo (`silhuetaSuperior`, mais o
 * assoalho plano em Y=0) pela MESMA composição que a cena 3D usa (Euler XYZ
 * do three.js: a rolagem em Z é aplicada ao ponto ANTES da inclinação em X) e
 * devolve quanto subir para o vértice mais baixo encostar em Y=0. Como o
 * mínimo de uma função linear sobre um polígono convexo sempre ocorre num
 * vértice, isso é EXATO em qualquer ângulo — ao contrário de interpolar por
 * poucos pontos de controle, que deixava o veículo atravessar o solo nos
 * ângulos intermediários da rolagem (entre ~15° e ~160°).
 */
export function alturaDeApoioExata(
  perfil: PerfilVeiculo,
  rolagemRad: number,
  inclinacaoLongitudinalRad = 0,
): number {
  const silhueta = silhuetaSuperior(perfil)
  let minY = Infinity
  for (const x of [-perfil.largura / 2, perfil.largura / 2]) {
    for (const [z, yTopo] of silhueta) {
      for (const y of [0, yTopo]) {
        const girado = girarEmTorno(vetor(x, y, z), vetor(0, 0, 1), rolagemRad)
        const inclinado = girarEmTorno(girado, vetor(1, 0, 0), inclinacaoLongitudinalRad)
        if (inclinado.y < minY) minY = inclinado.y
      }
    }
  }
  return -minY
}

/** Cofre/motor apoia a frente do capotado no solo; a traseira fica elevada. */
export function alturaApoioCapotado(
  perfil: PerfilVeiculo,
  inclinacaoRad = INCLINACAO_CAPOTADO_RAD,
): number {
  return alturaDeApoioExata(perfil, Math.PI, inclinacaoRad)
}

export interface PostoDoVeiculo {
  /** rolagem em torno do eixo longitudinal (+Z), em radianos */
  rolagemRad: number
  /** quanto subir depois de girar, para o veículo voltar a apoiar no solo */
  alturaApoio: number
  /** inclinação longitudinal: frente baixa no capotado */
  inclinacaoLongitudinalRad: number
}

/**
 * Como o veículo é apoiado em cada configuração.
 *
 * A altura de apoio é derivada da própria carroceria: girado 90° é a meia-largura
 * que passa a ser altura; invertido é a face externa do teto que encosta no solo.
 * Nenhum dos dois é número escolhido a olho.
 */
export function postoDoVeiculo(
  perfil: PerfilVeiculo,
  configuracao: ConfiguracaoVeiculo,
): PostoDoVeiculo {
  switch (configuracao) {
    case 'sobreRodas':
      return { rolagemRad: 0, alturaApoio: 0, inclinacaoLongitudinalRad: 0 }
    case 'lateral':
      // tombado sobre o lado do motorista (+X): ver ROLAGEM_SOBRE_O_MOTORISTA_RAD
      return { rolagemRad: ROLAGEM_SOBRE_O_MOTORISTA_RAD, alturaApoio: perfil.largura / 2, inclinacaoLongitudinalRad: 0 }
    case 'invertido':
      return { rolagemRad: Math.PI, alturaApoio: alturaApoioCapotado(perfil), inclinacaoLongitudinalRad: INCLINACAO_CAPOTADO_RAD }
  }
}

/**
 * Rolagem que deita o veículo sobre o LADO DO MOTORISTA.
 *
 * Doutrina adotada: "lado do motorista" é o lado em que o motorista dirige, e
 * na maquete o motorista (e toda peça "Esquerda") fica em +X. Girar em torno
 * de +Z por um ângulo positivo LEVANTA +X; é o ângulo negativo que leva o lado
 * do motorista ao solo. Toda cena de veículo tombado usa esta constante.
 */
export const ROLAGEM_SOBRE_O_MOTORISTA_RAD = -Math.PI / 2

/** Sinal da rolagem que desce o lado do motorista (−1). */
export const SINAL_ROLAGEM_LADO_MOTORISTA = -1

/** Direção local que vira elevação vertical no mundo depois de rolar o veículo. */
export function direcaoElevacaoPaDeForno(
  configuracao: ConfiguracaoVeiculo,
): ReturnType<typeof vetor> {
  // deitado sobre +X (motorista), é o lado do passageiro (−X) que aponta para cima
  if (configuracao === 'lateral') return vetor(-1, 0, 0)
  if (configuracao === 'invertido') return vetor(0, -1, 0)
  return vetor(0, 1, 0)
}

/**
 * Colocação da vítima deitada no teto do veículo capotado.
 *
 * Correção do instrutor: no capotamento a retirada é feita como se a vítima
 * estivesse deitada de decúbito ventral ou dorsal NO TETO — não suspensa pelo
 * cinto. O teto virou o piso, e é sobre ele que o corpo está.
 *
 * Não exige rig novo: `reclinacaoRad` (criada para a prancha) em ±90° deita o
 * corpo, e a guinada de 180° escolhe para onde a cabeça aponta. Como o cálculo
 * roda no referencial do VEÍCULO, aqui "acima" é o teto: no decúbito dorsal as
 * costas encostam nele, então a frente da vítima aponta para -Y.
 *
 * O Z da pelve é o da âncora, onde a vítima estava sentada. Com isso o corpo
 * estendido de um ocupante dianteiro passa da borda dianteira do teto — o que é o
 * caso real, e é parte do que torna a retirada do dianteiro a mais difícil.
 */
export function colocacaoNoTeto(
  perfil: PerfilVeiculo,
  ancora: AncoraOcupante,
  proporcoes: ProporcoesVitima,
  decubito: Decubito,
  sentidoCabeca: SentidoCabeca = 'frente',
): ColocacaoVitima {
  const cabecaParaFrente = sentidoCabeca === 'frente'
  const dorsal = decubito === 'dorsal'
  return {
    posicaoPelve: vetor(
      ancora.posicao.x,
      yFaceInternaDoTeto(perfil) - proporcoes.profundidadeTronco / 2,
      ancora.posicao.z,
    ),
    // dorsal e ventral têm reclinações opostas; a guinada corrige o sentido da
    // cabeça, que de outro modo ficaria amarrado ao decúbito
    guinadaRad: dorsal === cabecaParaFrente ? 0 : Math.PI,
    reclinacaoRad: dorsal ? -Math.PI / 2 : Math.PI / 2,
  }
}

/**
 * Vítima deitada sobre a lateral baixa do veículo tombado.
 *
 * A carroceria será girada depois. No referencial do veículo, aproximamos a
 * pelve da porta esquerda (lado do motorista, +X) e deitamos o corpo no eixo
 * longitudinal; depois da rolagem de −90°, essa porta vira o piso da cena.
 */
export function colocacaoNaLateralBaixa(
  perfil: PerfilVeiculo,
  ancora: AncoraOcupante,
  proporcoes: ProporcoesVitima,
  decubito: Decubito,
  sentidoCabeca: SentidoCabeca = 'frente',
): ColocacaoVitima {
  const cabecaParaFrente = sentidoCabeca === 'frente'
  const dorsal = decubito === 'dorsal'
  // Decúbito dorsal no veículo tombado sobre a lateral esquerda (motorista, +X):
  // O corpo deita ao longo do eixo longitudinal (Z), apoiando as costas sobre a
  // lateral baixa (+X) e voltando o peito para cima (−X local, que vira +Y vertical no mundo).
  // A rolagem axial de ±90° em torno da coluna define o decúbito.
  const rolagemRad = dorsal ? -Math.PI / 2 : Math.PI / 2

  return {
    posicaoPelve: vetor(
      perfil.largura / 2 - proporcoes.profundidadeTronco / 2 - 0.03,
      ancora.posicao.y,
      ancora.posicao.z,
    ),
    guinadaRad: 0,
    reclinacaoRad: cabecaParaFrente ? -Math.PI / 2 : Math.PI / 2,
    rolagemRad,
  }
}

/** Colocação da vítima na superfície que virou apoio depois do acidente. */
export function colocacaoNaConfiguracao(
  perfil: PerfilVeiculo,
  ancora: AncoraOcupante,
  proporcoes: ProporcoesVitima,
  configuracao: ConfiguracaoVeiculo,
  decubito: Decubito,
  sentidoCabeca: SentidoCabeca = 'frente',
): ColocacaoVitima {
  if (configuracao === 'invertido') {
    return colocacaoNoTeto(perfil, ancora, proporcoes, decubito, sentidoCabeca)
  }
  if (configuracao === 'lateral') {
    return colocacaoNaLateralBaixa(perfil, ancora, proporcoes, decubito, sentidoCabeca)
  }
  return colocacaoNaAncora(ancora)
}

/**
 * Altura, em coordenadas do veículo, da superfície em que a vítima se apoia.
 *
 * Sobre as rodas é o piso do habitáculo; invertido é a face interna do teto, que
 * virou o piso. É o plano em que arco e trajetória ficam legíveis: desenhá-los no
 * piso do veículo invertido os jogaria para o alto da cena.
 */
export function yDoPlanoDeApoio(
  perfil: PerfilVeiculo,
  configuracao: ConfiguracaoVeiculo,
): number {
  return configuracao === 'invertido' ? yFaceInternaDoTeto(perfil) + 0.02 : perfil.pisoY - 0.26
}

/** A vítima está deitada (e não sentada) nesta configuração? */
export const vitimaDeitada = (configuracao: ConfiguracaoVeiculo): boolean =>
  configuracao !== 'sobreRodas'

/** Posição física de uma abertura quando o veículo repousa sobre o lado esquerdo. */
export type PosicaoAberturaNoTombado = 'contraSolo' | 'ladoAlto' | 'traseira'

export function posicaoAberturaNoTombado(
  lado: 'esquerdo' | 'direito' | 'central',
): PosicaoAberturaNoTombado {
  if (lado === 'esquerdo') return 'contraSolo'
  if (lado === 'direito') return 'ladoAlto'
  return 'traseira'
}
